/**
 * PaymentPlanFields.jsx
 *
 * FAZ 1 karma ödeme turu (2026-09-25) — paylaşılan "Peşinat + Kalan" ödeme
 * bloğu. Tüm satış formlarındaki (DersEkleKart, StudentEnrollmentsTab "Ders
 * Ata", AdminStudentSalePage, NewUserPage son adım, DanismanCockpitShared
 * "Dönüştür") eski tek "Ödeme Türü (Nakit/Kart/Havale)" seçimini bu iki
 * bölüme dönüştürür. Backend sözleşmesi (TEK KAYNAK, bkz.
 * backend/services/sales_service.py + backend/routes/admin.py, 2026-09-25
 * "FAZ 1 karma ödeme" yorumları):
 *
 *   - Peşinat: `down_payment` + `down_payment_method` (admin: cash|bank_
 *     transfer) — admin'de satış anında GERÇEKTEN tahsil edilir (kasa/banka
 *     hesabı zorunlu, POS reddedilir). Danışman rolünde hesap SEÇİLMEZ —
 *     backend down_payment'ı sunucu tarafında tarihli bir havale BORÇ
 *     satırına çevirir (tahsilat yok, "danışman para almaz" kuralı korunur).
 *   - Kalan: YA N adet `method:"bank_transfer"` satırı (aylık, ayın aynı
 *     günü — `InstallmentPreview`) YA DA kalanın TAMAMI tek bir
 *     `method:"credit_card"` satırı (Moka'ya tek çekim, taksidi öğrenci
 *     Moka'nın ödeme sayfasında seçer — bizim tarafımızda ASLA bölünmez).
 *   - Peşinat = toplam tutar ise kalan bölüm tamamen gizlenir (eski "peşin
 *     nakit tam tahsil" senaryosunun yeni karşılığı).
 *
 * KRİTİK — top-level `payment_method` HER ZAMAN "deferred" gönderilir
 * (kalan > 0 iken): backend'in `_INSTANT_METHODS = ("cash",)` kapısı
 * `payment_method="cash"` + `installments<=1` + plan yokken satışı "peşin,
 * tüm tutar şimdi tahsil edildi" olarak açar — kalan bir borç varken bu
 * YANLIŞ olur. "deferred" kod tabanında zaten belgeli, güvenli bir değerdir
 * (bkz. routes/admin.py: `payment_method = "deferred" → VADELİ`).
 *
 * DANIŞMAN + PEŞİNAT KOMBİNASYONU — backend `first_due_date` alanını bu
 * durumda PEŞİNAT tarihi olarak yeniden okur (routes/admin.py "FAZ 1 karma
 * ödeme" bloğu, `_pesinat_due = first_due_date or tr_today_str()`); kalanın
 * kendi vadesi implicit (installments+first_due_date) yoldan gönderilirse
 * backend +30 gün varsayılanına düşer — danışmanın seçtiği "kalan ilk taksit
 * tarihi" SESSİZCE YOK SAYILIR. Bu yüzden bu kombinasyonda (aşağıda
 * `danismanPesinatliKalan`) kalan HER ZAMAN açık bir `installment_plan`
 * olarak gönderilir (backend otomatik olarak peşinat satırını başına ekler);
 * düzenlenmemiş (eşit bölme) hal de aynı `/admin/sales/installment-preview`
 * uç noktasından materialize edilir (ekranda GÖRÜNEN ile GÖNDERİLEN asla
 * ıraksamaz — deterministik, aynı endpoint/parametreler).
 *
 * `onChange(fields, meta)` her ilgili değişiklikte çağrılır:
 *   - `fields`: submit body'sine DOĞRUDAN spread edilecek parçalar
 *     (`down_payment`, `down_payment_method`, `bank_id`, `first_due_date`,
 *     `installments`, `installment_plan`, `payment_method`).
 *   - `meta.isValid`: false iken kaydet butonu kilitlenmeli.
 *   - `meta.isCollectingNow`: yalnızca admin — peşinat için hesap seçimi
 *     zorunlu mu (bilgi amaçlı, `fields`/`isValid` zaten bunu yansıtır).
 *   - `meta.downPayment` / `meta.remaining`: özet gösterimi için.
 */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Wallet, CreditCard, Landmark, AlertCircle, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { fmtMoney } from '../../utils/money';
import { trNowLocalDateInput, trAddBusinessDays, ILK_TAKSIT_MAX_IS_GUNU, KART_CEKIM_MAX_GUN } from '../../utils/trDateTime';
import InstallmentPreview from './InstallmentPreview';

const LBL_CLS = 'text-[11px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide';
const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none transition-colors disabled:opacity-50';
const DEBOUNCE_MS = 300;

// Danışman peşinat modu — TEK KAYNAK. Danışman rolünde peşinat backend
// tarafında TAHSİL EDİLMEZ ("danışman para almaz" kuralı) — down_payment,
// sunucuda tarihli bir `method:"bank_transfer"` BORÇ satırına çevrilir (bkz.
// dosya başı yorumu + routes/admin.py "FAZ 1 karma ödeme" bloğu, `_pesinat_
// due`). Bu, şu an desteklenen TEK mod; ileride farklı bir davranış
// (örn. danışmana da anlık tahsilat izni) gerekirse SADECE bu sabit ve onu
// okuyan tek `if` (aşağıda, payload effect'i içinde) değişir.
const DANISMAN_PESINAT_MODU = 'planli_havale';

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function addDaysStr(dateStr, days) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function MethodToggle({ options, value, onChange, disabled }) {
  return (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          className={`flex items-center justify-center gap-1.5 py-2 rounded-xl border-2 text-xs font-bold transition-all disabled:opacity-50 ${
            value === opt.value
              ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white'
              : 'bg-white border-[var(--border-light)] text-[var(--text-muted)]'
          }`}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function PaymentPlanFields({
  role, totalAmount, banks, banksLoading, disabled, className, onChange,
}) {
  const { authHeaders } = useAuth();
  const { t, lang } = useLanguage();

  const isAdminRole = role === 'admin';
  const totalNum = round2(totalAmount);

  const bugunTR = trNowLocalDateInput();
  const ilkTaksitSonTarih = trAddBusinessDays(bugunTR, ILK_TAKSIT_MAX_IS_GUNU);

  // ── Peşinat ────────────────────────────────────────────────────────────
  const [downPaymentInput, setDownPaymentInput] = useState('');
  const [downPaymentMethod, setDownPaymentMethod] = useState('cash'); // admin only
  const [bankId, setBankId] = useState(''); // admin only
  const [downPaymentDate, setDownPaymentDate] = useState(bugunTR); // danisman only

  const downPayment = round2(parseFloat(downPaymentInput) || 0);
  const hasDownPayment = downPayment > 0.009;
  const remaining = totalNum > 0 ? round2(Math.max(0, totalNum - downPayment)) : 0;
  const remainingIsZero = remaining <= 0.009;

  // ── Kalan ──────────────────────────────────────────────────────────────
  const [remainingMethod, setRemainingMethod] = useState('bank_transfer');
  const [installmentCount, setInstallmentCount] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState('');
  const [cardDueDate, setCardDueDate] = useState('');
  const [customPlan, setCustomPlan] = useState(null);
  const [customPlanDiff, setCustomPlanDiff] = useState(0);
  const [customPlanHasError, setCustomPlanHasError] = useState(false);

  // Danışman + peşinat kombinasyonu — bkz. dosya başı yorumu.
  const danismanPesinatliKalan = !isAdminRole && hasDownPayment && !remainingIsZero;

  const kalanMinDate = danismanPesinatliKalan ? addDaysStr(downPaymentDate, 1) : bugunTR;
  const kalanMaxDate = danismanPesinatliKalan ? undefined : ilkTaksitSonTarih;
  // 2026-09-27 fix: kart çekim tarihi "ilk taksit" değildir (bkz.
  // backend services/sales_service.py::validate_card_charge_date_or_400) —
  // 3 iş günü kuralı yerine bugün + KART_CEKIM_MAX_GUN TAKVİM günü uygulanır.
  const kartCekimMaxDate = danismanPesinatliKalan ? undefined : addDaysStr(bugunTR, KART_CEKIM_MAX_GUN);

  // installmentCount 1'e düşünce / yöntem değişince InstallmentPreview
  // unmount olabilir — customPlan'ı burada da sıfırlıyoruz (stale plan
  // submit'e sızmasın).
  useEffect(() => {
    if (remainingMethod !== 'bank_transfer' || installmentCount <= 1) {
      setCustomPlan(null);
      setCustomPlanDiff(0);
      setCustomPlanHasError(false);
    }
  }, [remainingMethod, installmentCount]);

  // Kalanın ilk/tek vade tarihi + kart çekim tarihi varsayılanı — yalnızca
  // kullanıcı henüz dokunmadıysa (boşsa) set edilir.
  useEffect(() => {
    setFirstDueDate((prev) => (prev ? prev : kalanMinDate));
    setCardDueDate((prev) => (prev ? prev : kalanMinDate));
  }, [kalanMinDate]);

  // Danışman + peşinat + havale kombinasyonunda, DÜZENLENMEMİŞ (eşit
  // bölünmüş) kalan planı `/admin/sales/installment-preview`'dan materialize
  // edilir — ekranda gösterilen (InstallmentPreview) ile gönderilen AYNI
  // endpoint/parametreden geldiği için asla ıraksamaz.
  const [materializedRows, setMaterializedRows] = useState(null);
  const [materializing, setMaterializing] = useState(false);
  const [materializeError, setMaterializeError] = useState('');
  const materializeTimerRef = useRef(null);
  const materializeReqIdRef = useRef(0);

  useEffect(() => {
    if (!danismanPesinatliKalan || remainingMethod !== 'bank_transfer' || customPlan !== null) {
      setMaterializedRows(null);
      setMaterializing(false);
      setMaterializeError('');
      return undefined;
    }
    if (materializeTimerRef.current) clearTimeout(materializeTimerRef.current);
    const myReqId = ++materializeReqIdRef.current;
    setMaterializing(true);
    setMaterializeError('');
    materializeTimerRef.current = setTimeout(async () => {
      try {
        const res = await apiClient.post(
          '/admin/sales/installment-preview',
          {
            total_amount: remaining,
            down_payment: 0,
            installments: Math.max(installmentCount, 1),
            first_due_date: firstDueDate || null,
          },
          { headers: authHeaders() }
        );
        if (materializeReqIdRef.current === myReqId) {
          setMaterializedRows((res.data?.installments || []).map((r) => ({ amount: r.amount, due_date: r.due_date })));
          setMaterializing(false);
        }
      } catch (err) {
        if (materializeReqIdRef.current === myReqId) {
          setMaterializeError(getApiErrorMessage(err, lang));
          setMaterializing(false);
        }
      }
    }, DEBOUNCE_MS);
    return () => { if (materializeTimerRef.current) clearTimeout(materializeTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [danismanPesinatliKalan, remainingMethod, customPlan, remaining, installmentCount, firstDueDate, lang]);

  const collectionBanks = banks || [];
  const isCollectingNow = isAdminRole && hasDownPayment;

  // ── Payload + doğrulama -> onChange ──────────────────────────────────────
  useEffect(() => {
    const fields = {};
    let isValid = true;

    if (totalNum <= 0.009) {
      onChange?.(fields, { isValid: true, isCollectingNow: false, downPayment: 0, remaining: 0 });
      return;
    }

    if (hasDownPayment) {
      fields.down_payment = downPayment;
      if (isAdminRole) {
        fields.down_payment_method = downPaymentMethod;
        if (!bankId) isValid = false;
        else fields.bank_id = bankId;
      } else {
        if (!downPaymentDate) isValid = false;
        // Danışman + peşinat -> backend first_due_date'i PEŞİNAT tarihi
        // olarak okur (bkz. dosya başı yorumu); admin'de first_due_date
        // kalanın KENDİ vadesidir, peşinatla hiç ilgisi yoktur (aşağıda
        // ayrıca set edilir). DANISMAN_PESINAT_MODU tek dallanma noktası —
        // bkz. dosya başı sabit tanımı.
        if (DANISMAN_PESINAT_MODU === 'planli_havale') {
          fields.first_due_date = downPaymentDate;
        }
      }
    }

    if (hasDownPayment || !remainingIsZero) {
      // bkz. dosya başı yorumu — peşinat ve/veya kalan varken satış HER ZAMAN
      // vadeli/borç açık gönderilir (top-level "payment_method" boş/"cash"
      // kalırsa backend `_INSTANT_METHODS` kapısından "peşin, tüm tutar
      // şimdi tahsil edildi" sanır; `down_payment>0` + peşin kombinasyonu
      // ise 400 `pesinat_pesin_satista_gecersiz` ile reddedilir). Peşinat
      // TOPLAMA eşitse (remainingIsZero) bile bu ALAN gönderilmelidir —
      // o durumda tüm tutar `down_payment` üzerinden, "deferred" + peşinat
      // yoluyla toplanır (eski "cash, tam peşin" akışının yeni karşılığı).
      fields.payment_method = 'deferred';
    }

    if (!remainingIsZero) {
      if (remainingMethod === 'credit_card') {
        if (!cardDueDate) isValid = false;
        else fields.installment_plan = [{ amount: remaining, due_date: cardDueDate, method: 'credit_card' }];
      } else if (danismanPesinatliKalan) {
        const rows = customPlan || materializedRows;
        if (!rows || materializing || !!materializeError) {
          isValid = false;
        } else {
          const sum = round2(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0));
          if (Math.abs(sum - remaining) >= 0.01) isValid = false;
          fields.installment_plan = rows.map((r) => ({
            amount: Number(r.amount) || 0, due_date: r.due_date, method: 'bank_transfer',
          }));
        }
      } else if (installmentCount > 1) {
        if (customPlan) {
          if (Math.abs(customPlanDiff) >= 0.01 || customPlanHasError) isValid = false;
          fields.installment_plan = customPlan.map((r) => ({
            amount: r.amount, due_date: r.due_date, method: 'bank_transfer',
          }));
        } else {
          if (!firstDueDate) isValid = false;
          fields.installments = installmentCount;
          fields.first_due_date = firstDueDate;
        }
      } else {
        if (!firstDueDate) isValid = false;
        fields.installments = 1;
        fields.first_due_date = firstDueDate;
      }
    }

    onChange?.(fields, { isValid, isCollectingNow, downPayment, remaining });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalNum, hasDownPayment, downPayment, downPaymentMethod, bankId, downPaymentDate,
    remainingIsZero, remaining, remainingMethod, cardDueDate, danismanPesinatliKalan,
    customPlan, materializedRows, materializing, materializeError,
    installmentCount, customPlanDiff, customPlanHasError, firstDueDate, isAdminRole,
  ]);

  const isDisabled = !!disabled;

  return (
    <div className={`space-y-4 ${className || ''}`} data-testid="payment-plan-fields">
      {/* Peşinat */}
      <div className="border-2 border-[var(--border-light)] rounded-xl p-3 space-y-2.5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1.5">
          <Wallet size={13} /> {t('sales.paymentPlan.down_payment_title')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <div>
            <label className={LBL_CLS}>{t('sales.paymentPlan.down_payment_amount_label')}</label>
            <input
              type="number"
              min={0}
              max={totalNum || undefined}
              step={0.01}
              value={downPaymentInput}
              onChange={(e) => setDownPaymentInput(e.target.value)}
              disabled={isDisabled}
              placeholder="0"
              className={INPUT_CLS}
              data-testid="payment-plan-down-payment-amount"
            />
          </div>
          {isAdminRole ? (
            <div>
              <label className={LBL_CLS}>{t('sales.paymentPlan.down_payment_method_label')}</label>
              <MethodToggle
                disabled={isDisabled || !hasDownPayment}
                value={downPaymentMethod}
                onChange={setDownPaymentMethod}
                options={[
                  { value: 'cash', label: t('sales.paymentPlan.method_cash'), testId: 'payment-plan-down-method-cash' },
                  { value: 'bank_transfer', label: t('sales.paymentPlan.method_bank_transfer'), testId: 'payment-plan-down-method-bank-transfer' },
                ]}
              />
            </div>
          ) : (
            <div>
              <label className={LBL_CLS}>{t('sales.paymentPlan.down_payment_date_label')}</label>
              <input
                type="date"
                value={downPaymentDate}
                min={bugunTR}
                max={ilkTaksitSonTarih}
                disabled={isDisabled || !hasDownPayment}
                onChange={(e) => setDownPaymentDate(e.target.value)}
                className={INPUT_CLS}
                data-testid="payment-plan-down-payment-date"
              />
            </div>
          )}
        </div>

        {isAdminRole && hasDownPayment && (
          <div>
            <label className={LBL_CLS}>{t('sales.bank_label')}</label>
            <select
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
              disabled={isDisabled || banksLoading}
              className={INPUT_CLS}
              data-testid="payment-plan-bank-select"
            >
              <option value="">{t('sales.bank_placeholder')}</option>
              {collectionBanks.map((b) => (
                <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
              ))}
            </select>
            <p className="text-[10px] mt-1 text-[var(--text-muted)]">{t('sales.bank_required_hint')}</p>
            {!banksLoading && collectionBanks.length === 0 && (
              <p className="text-[10px] text-rose-600 mt-1 font-bold">{t('sales.bank_none_available')}</p>
            )}
          </div>
        )}

        {!isAdminRole && hasDownPayment && (
          <p className="text-[10px] text-[var(--text-muted)]">{t('sales.paymentPlan.down_payment_danisman_note')}</p>
        )}
      </div>

      {/* Kalan */}
      {hasDownPayment && remainingIsZero ? (
        <p
          className="text-xs font-bold text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl px-3 py-2"
          data-testid="payment-plan-full-down-payment-notice"
        >
          {t('sales.paymentPlan.full_down_payment_notice')}
        </p>
      ) : (
        <div className="border-2 border-[var(--border-light)] rounded-xl p-3 space-y-2.5">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t('sales.paymentPlan.remaining_title')}
            </p>
            <span className="text-sm font-bold" data-testid="payment-plan-remaining-amount">
              {fmtMoney(remaining)}
            </span>
          </div>

          <MethodToggle
            disabled={isDisabled}
            value={remainingMethod}
            onChange={setRemainingMethod}
            options={[
              { value: 'bank_transfer', icon: <Landmark size={13} />, label: t('sales.paymentPlan.remaining_method_bank_transfer'), testId: 'payment-plan-remaining-method-bank-transfer' },
              { value: 'credit_card', icon: <CreditCard size={13} />, label: t('sales.paymentPlan.remaining_method_credit_card'), testId: 'payment-plan-remaining-method-credit-card' },
            ]}
          />

          {remainingMethod === 'bank_transfer' ? (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className={LBL_CLS}>{t('sales.paymentPlan.installments_count_label')}</label>
                  <input
                    type="number"
                    min={1}
                    max={36}
                    value={installmentCount}
                    onChange={(e) => setInstallmentCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    disabled={isDisabled}
                    className={INPUT_CLS}
                    data-testid="payment-plan-installments-count"
                  />
                </div>
                <div>
                  <label className={LBL_CLS}>{t('sales.paymentPlan.first_due_date_label')}</label>
                  <input
                    type="date"
                    value={firstDueDate}
                    min={kalanMinDate}
                    max={kalanMaxDate}
                    onChange={(e) => setFirstDueDate(e.target.value)}
                    disabled={isDisabled}
                    className={INPUT_CLS}
                    data-testid="payment-plan-first-due-date"
                  />
                </div>
              </div>

              {installmentCount > 1 && (
                <InstallmentPreview
                  totalAmount={remaining}
                  downPayment={0}
                  installments={installmentCount}
                  firstDueDate={firstDueDate}
                  editable
                  onPlanChange={(plan, diff, hasError) => {
                    setCustomPlan(plan);
                    setCustomPlanDiff(diff);
                    setCustomPlanHasError(!!hasError);
                  }}
                />
              )}

              {danismanPesinatliKalan && materializing && (
                <div className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]" data-testid="payment-plan-materializing">
                  <Loader2 size={11} className="animate-spin" />
                  {t('sales.installmentPreview.loading')}
                </div>
              )}
              {danismanPesinatliKalan && !materializing && materializeError && (
                <div className="flex items-center gap-1.5 text-xs text-rose-600 font-bold" data-testid="payment-plan-materialize-error">
                  <AlertCircle size={12} className="flex-shrink-0" />
                  {materializeError}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className={LBL_CLS}>{t('sales.paymentPlan.card_due_date_label')}</label>
                  <input
                    type="date"
                    value={cardDueDate}
                    min={kalanMinDate}
                    max={kartCekimMaxDate}
                    onChange={(e) => setCardDueDate(e.target.value)}
                    disabled={isDisabled}
                    className={INPUT_CLS}
                    data-testid="payment-plan-card-due-date"
                  />
                </div>
                <div>
                  <label className={LBL_CLS}>{t('sales.paymentPlan.remaining_amount_label')}</label>
                  <input
                    type="text"
                    readOnly
                    value={fmtMoney(remaining)}
                    className={INPUT_CLS + ' bg-gray-50 text-[var(--text-muted)]'}
                    data-testid="payment-plan-card-amount"
                  />
                </div>
              </div>
              <p className="text-xs font-semibold text-amber-700 bg-amber-50 border-2 border-amber-200 rounded-xl px-3 py-2">
                {t('sales.paymentPlan.card_notice')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

PaymentPlanFields.propTypes = {
  role: PropTypes.oneOf(['admin', 'danisman']).isRequired,
  totalAmount: PropTypes.number,
  banks: PropTypes.array,
  banksLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
};

PaymentPlanFields.defaultProps = {
  totalAmount: 0,
  banks: [],
  banksLoading: false,
  disabled: false,
  className: '',
  onChange: null,
};
